/**
 * Billing > Aging (Phase 21 / Task 21.7d, on 21.6b's API): what customers still owe, by due date
 * and by days past due, in local days of the billing time zone. GET /aging; the totals are the
 * server's SQL sums. The CSV is GET /aging.csv. "Remind all overdue" is POST
 * /invoices/remind-overdue: it sends at most 25 per click and says how many remain, so the page
 * offers the next batch (Session 6). A customer is never reminded twice in a day.
 */
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ariaSort, listFrom, sortRows, useUrlParam, useUrlSort } from '../../lib/url-state';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { SortButton } from 'src/components/ui/sortable-head';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  useAging,
  useBillingCustomers,
  useInvoiceMutation,
  type AgingBucket,
  type DueClass,
  type RemindOverdueResponse,
} from './billing-hooks';
import { billingErrorText } from './billing-format';
import { money } from './invoice-format';
import { AGING_BUCKET_LABELS, DUE_CLASS_LABELS, localStamp, remindSummary } from './payment-format';
import { formatNumber } from '@lodex-pro/shared/number-format';

const BUCKETS: readonly AgingBucket[] = ['CURRENT', 'D1_30', 'D31_60', 'D61_90', 'D90_PLUS'];
const DUE: readonly DueClass[] = ['OVERDUE', 'DUE_THIS_WEEK', 'DUE_LATER'];
/** The columns the aging table sorts by (OPEN-204); the API sends them by due date. */
const AGING_SORTS = [
  'invoice',
  'customer',
  'due',
  'aging',
  'balance',
  'pending',
  'lastReminded',
] as const;
const cell: React.CSSProperties = { padding: '6px 8px' };
const tile: React.CSSProperties = {
  borderRadius: '12px',
  backgroundColor: 'var(--color-bg-tertiary)',
  padding: '8px 12px',
  minWidth: '120px',
};

function CsvButton({ customer }: { customer: string }): React.JSX.Element {
  const opts = useApiOpts();
  return (
    <Button
      type="button"
      variant="outline"
      onClick={(): void => {
        const q = customer === '' ? '' : `?customer=${customer}`;
        void authedFetch(opts, `/v1/platform/billing/aging.csv${q}`)
          .then(async (res) => {
            if (!res.ok) throw new Error(String(res.status));
            const name =
              /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
              'paelode-aging.csv';
            const url = URL.createObjectURL(await res.blob());
            const a = document.createElement('a');
            a.href = url;
            a.download = name;
            // #974 review LOW-3: attached (some Firefox versions need it) and revoked a moment
            // later, so Safari and older Firefox do not cancel the download.
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          })
          .catch(() => toast.error('Could not download the aging CSV.'));
      }}
    >
      Download CSV
    </Button>
  );
}

/**
 * Remind all overdue (21.6b's POST /invoices/remind-overdue), in batches of at most 25.
 *
 * #974 review MED-1: a batch and the "next batch" after it always go to the scope the FIRST
 * click was for, kept with the result. The component is also keyed by the customer filter, so
 * changing the filter drops the old result, and the next batch asks again, naming the scope.
 * MED-2: a failure shows a plain sentence, never the server's error text. LOW-6: the count is
 * "up to N", because invoices already reminded today are skipped.
 */
function RemindOverdue({
  customer,
  scopeLabel,
  overdue,
}: {
  customer: string;
  scopeLabel: string;
  overdue: number;
}): React.JSX.Element {
  const [confirming, setConfirming] = React.useState<'FIRST' | 'NEXT' | null>(null);
  const [last, setLast] = React.useState<{
    res: RemindOverdueResponse;
    customer: string;
    scopeLabel: string;
  } | null>(null);
  const remind = useInvoiceMutation<{ customerTenantId?: string }, RemindOverdueResponse>(
    '/invoices/remind-overdue',
    'POST',
    {
      onSuccess: () => setConfirming(null),
      onError: (err) => toast.error(billingErrorText(err, 'Could not send the reminders.')),
    },
  );
  const send = (scope: { customer: string; scopeLabel: string }): void =>
    remind.mutate(scope.customer === '' ? {} : { customerTenantId: scope.customer }, {
      onSuccess: (res) => {
        setLast({ res, ...scope });
        setConfirming(null);
        if (res.counts.failed > 0) toast.error(remindSummary(res));
        else toast.success(remindSummary(res));
      },
    });
  const failed = last === null ? [] : last.res.results.filter((r) => r.status === 'FAILED');
  return (
    <div style={{ marginBottom: '12px' }}>
      {confirming === null && (
        <Button
          type="button"
          disabled={overdue === 0 || remind.isPending}
          onClick={(): void => setConfirming('FIRST')}
        >
          Remind all overdue
        </Button>
      )}
      {confirming === 'FIRST' && (
        <div
          role="alert"
          data-testid="remind-confirm"
          style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <span style={subText}>
            This emails a payment reminder, with the invoice PDF, for up to {String(overdue)}{' '}
            overdue invoice{overdue === 1 ? '' : 's'} of {scopeLabel} (any already reminded today
            are skipped), 25 at a time.
          </span>
          <Button
            type="button"
            disabled={remind.isPending}
            onClick={(): void => send({ customer, scopeLabel })}
          >
            Send reminders
          </Button>
          <Button type="button" variant="ghost" onClick={(): void => setConfirming(null)}>
            Cancel
          </Button>
        </div>
      )}
      {last !== null && (
        <div data-testid="remind-result" style={{ ...subText, marginTop: '8px' }}>
          <p style={{ margin: 0 }}>
            {remindSummary(last.res)} ({last.scopeLabel})
          </p>
          {last.res.remaining > 0 && confirming !== 'NEXT' && (
            <Button
              type="button"
              variant="outline"
              disabled={remind.isPending}
              onClick={(): void => setConfirming('NEXT')}
            >
              {String(last.res.remaining)} more — Send next batch
            </Button>
          )}
          {last.res.remaining > 0 && confirming === 'NEXT' && (
            <div
              role="alert"
              data-testid="remind-next-confirm"
              style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}
            >
              <span>Send the next batch of up to 25 reminders for {last.scopeLabel}?</span>
              <Button
                type="button"
                disabled={remind.isPending}
                onClick={(): void => send({ customer: last.customer, scopeLabel: last.scopeLabel })}
              >
                Send next batch
              </Button>
              <Button type="button" variant="ghost" onClick={(): void => setConfirming(null)}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}
      {failed.length > 0 && (
        <ul data-testid="remind-failures" style={{ ...subText, color: 'var(--color-danger)' }}>
          {failed.map((r) => (
            <li key={r.invoiceId}>
              {r.invoiceNumber ?? 'An invoice'}: the reminder could not be sent. Try again later, or
              open the invoice and use Send payment reminder.
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AgingPage(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // OPEN-191 — the customer filter lives in the URL, so an invoice's Back returns to it.
  const [customer, setCustomer] = useUrlParam('customer', '', []);
  const customers = useBillingCustomers();
  const { data, isLoading, isError } = useAging(customer);
  const { sort, toggle } = useUrlSort(AGING_SORTS, { key: 'due', dir: 'asc' }, []);
  const invoices = sortRows(data?.invoices ?? [], sort, (i, k) => {
    switch (k) {
      case 'invoice':
        return i.invoiceNumber;
      case 'customer':
        return i.customerName ?? i.customerTenantId;
      case 'due':
        return i.dueDate;
      case 'aging':
        return i.daysPastDue;
      case 'balance':
        return i.balanceCents;
      case 'pending':
        return i.pendingCents;
      case 'lastReminded':
        return i.lastRemindedAt;
    }
  });
  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'end', marginBottom: '12px' }}>
        <FormRow label="Customer" htmlFor="aging-customer">
          <select
            id="aging-customer"
            value={customer}
            onChange={(e): void => setCustomer(e.target.value)}
            style={fieldStyle}
          >
            <option value="">All customers</option>
            {(customers.data?.customers ?? []).map((c) => (
              <option key={c.tenantId} value={c.tenantId}>
                {c.tenantName}
              </option>
            ))}
          </select>
        </FormRow>
        <CsvButton customer={customer} />
      </div>
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load the aging report.
        </p>
      ) : (
        <>
          <p style={{ ...subText, marginTop: 0 }}>
            As of {data.asOf} ({data.timezone}) · {formatNumber(data.totals.all.count)} open · owed{' '}
            {money(data.totals.all.balanceCents)}
            {data.totals.pendingCents > 0 && (
              <> · {money(data.totals.pendingCents)} of it is a bank payment settling</>
            )}
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
            {DUE.map((d) => (
              <div key={d} style={tile} data-testid={`due-${d}`}>
                <div style={subText}>{DUE_CLASS_LABELS[d]}</div>
                <div style={{ fontWeight: 600 }}>
                  {money(data.totals.byDueClass[d].balanceCents)}
                </div>
                <div style={subText}>{String(data.totals.byDueClass[d].count)} invoices</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
            {BUCKETS.map((b) => (
              <div key={b} style={tile} data-testid={`bucket-${b}`}>
                <div style={subText}>{AGING_BUCKET_LABELS[b]}</div>
                <div style={{ fontWeight: 600 }}>{money(data.totals.byBucket[b].balanceCents)}</div>
                <div style={subText}>{String(data.totals.byBucket[b].count)} invoices</div>
              </div>
            ))}
          </div>
          <RemindOverdue
            key={customer}
            customer={customer}
            scopeLabel={
              customer === ''
                ? 'all customers'
                : (customers.data?.customers.find((c) => c.tenantId === customer)?.tenantName ??
                  'this customer')
            }
            overdue={data.totals.byDueClass.OVERDUE.count}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ textAlign: 'left', ...subText }}>
                <th style={cell} aria-sort={ariaSort(sort, 'invoice')}>
                  <SortButton column="invoice" sort={sort} onSort={toggle}>
                    Invoice
                  </SortButton>
                </th>
                <th style={cell} aria-sort={ariaSort(sort, 'customer')}>
                  <SortButton column="customer" sort={sort} onSort={toggle}>
                    Customer
                  </SortButton>
                </th>
                <th style={cell} aria-sort={ariaSort(sort, 'due')}>
                  <SortButton column="due" sort={sort} onSort={toggle}>
                    Due
                  </SortButton>
                </th>
                <th style={cell} aria-sort={ariaSort(sort, 'aging')}>
                  <SortButton column="aging" sort={sort} onSort={toggle}>
                    Aging
                  </SortButton>
                </th>
                <th style={{ ...cell, textAlign: 'right' }} aria-sort={ariaSort(sort, 'balance')}>
                  <SortButton column="balance" sort={sort} onSort={toggle}>
                    Balance
                  </SortButton>
                </th>
                <th style={{ ...cell, textAlign: 'right' }} aria-sort={ariaSort(sort, 'pending')}>
                  <SortButton column="pending" sort={sort} onSort={toggle}>
                    Pending ACH
                  </SortButton>
                </th>
                <th style={cell} aria-sort={ariaSort(sort, 'lastReminded')}>
                  <SortButton column="lastReminded" sort={sort} onSort={toggle}>
                    Last reminded
                  </SortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr
                  key={i.invoiceId}
                  data-testid={`aging-${i.invoiceId}`}
                  onClick={(): void => {
                    void navigate(`/billing/invoices/${i.invoiceId}`, {
                      state: listFrom(location),
                    });
                  }}
                  style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border-primary)' }}
                >
                  <td style={cell}>{i.invoiceNumber}</td>
                  <td style={cell}>{i.customerName ?? i.customerTenantId}</td>
                  <td style={cell}>
                    {i.dueDate}{' '}
                    {i.dueClass === 'OVERDUE' && <Pill tone="warning" label="Overdue" />}
                  </td>
                  <td style={cell}>
                    {i.daysPastDue > 0
                      ? `${AGING_BUCKET_LABELS[i.bucket]} (${String(i.daysPastDue)} d)`
                      : AGING_BUCKET_LABELS[i.bucket]}
                  </td>
                  <td style={{ ...cell, textAlign: 'right' }}>{money(i.balanceCents)}</td>
                  <td style={{ ...cell, textAlign: 'right', ...subText }}>
                    {i.pendingCents > 0 ? money(i.pendingCents) : '—'}
                  </td>
                  <td style={{ ...cell, ...subText }}>
                    {i.lastRemindedAt === null ? '—' : localStamp(i.lastRemindedAt, data.timezone)}
                  </td>
                </tr>
              ))}
              {data.invoices.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ ...cell, ...subText }}>
                    Nothing is owed.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
